<template>
  <Form
    class="flex flex-col gap-6 w-full"
    :resolver="aboutUsResolver"
    :initialValues="initialValues"
    :validateOnBlur="true"
    :validateOnValueUpdate="true"
    ref="aboutUsForm"
    data-test="about-us-form"
    v-slot="$form"
    v-focustrap
  >
    <LabeledField
      label="About Us"
      :field="$form.aboutUs"
      data-test="about-us-field"
    >
      <Textarea
        name="aboutUs"
        autoResize
        fluid
        data-test="about-us-textarea"
      />
      <div class="flex text-sm text-gray-500 mt-1">
        <i class="fas fa-info-circle mr-2 mt-[2px]"></i>
        <div data-test="about-us-char-count">{{ ($form.aboutUs?.value || '').length }}/560 Characters</div>
      </div>
    </LabeledField>
    <LabeledField
      label="Custom URL"
      :field="$form.customUrl"
      data-test="custom-url-field"
    >
      <InputGroup>
        <InputGroupAddon>
          <span
            class="text-gray-500"
            data-test="truncated-domain"
            >{{ truncatedDomain }}</span
          >
        </InputGroupAddon>
        <InputText
          name="customUrl"
          type="url"
          v-keyfilter="/[A-Za-z\-]/"
          fluid
          data-test="custom-url-input"
        />
      </InputGroup>
      <div class="flex text-sm text-gray-500 my-2">
        <i class="fas fa-info-circle mr-2 mt-[2px]"></i>
        <div data-test="custom-url-info">
          Custom URL will be used to access your company's giving page. It may contain only letters and hyphens.<br />
          Example: <span class="font-semibold">company-name</span>
        </div>
      </div>
    </LabeledField>
    <LabeledField
      label="About Us Image"
      :field="$form.aboutUsImage"
      data-test="about-us-image-field"
    >
      <FormField
        v-slot="$field"
        name="aboutUsImage"
      >
        <ImageUpload
          auto
          :minSize="400"
          :maxSize="1024"
          :value="$field"
          @uploaded="
            (data) => {
              $form.aboutUsImage.value = data;
            }
          "
          data-test="about-us-image-upload"
        />
        <div class="flex text-sm text-gray-500 my-2">
          <i class="fas fa-info-circle mr-2 mt-[2px]"></i>
          <div data-test="about-us-image-info">
            Images should have a minimum width of 598px and look best in a 4:3 aspect<br />
            Accepted Image Types: JPEG, PNG
          </div>
        </div>
      </FormField>
    </LabeledField>
  </Form>
</template>

<script setup>
import { ref, onBeforeMount, onMounted, onBeforeUnmount, computed } from 'vue';
import { z } from 'zod';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { useCompanyAdminStore } from '@/stores/CompanyAdminStore.js';
import { useDonorStore } from '@/stores/DonorStore.js';
import ImageUpload from '@/components/forms/ImageUpload.vue';
import LabeledField from '@/components/forms/LabeledField.vue';
import debounceAsync from '../../helpers/debounceAsync';

const debouncedCheckUrlAvailability = debounceAsync(async (customUrl) => {
  return await useCompanyAdminStore().checkUrlAvailability(customUrl, 'initial');
}, 300);

const aboutUsForm = ref(null);
// const aboutUsResolver = zodResolver(
//   z.object({
//     aboutUs: z.string().max(560, 'Maximum 560 characters allowed'),
// customUrl: z
//   .string()
//   .min(1, 'Custom URL is required')
//   .max(64)
//   .regex(/^[A-Za-z\-]+$/, 'Custom URL may contain only letters and hyphens'),
//     .refine(
//       async (customUrl) => {
//         return await companyAdminStore.checkUrlAvailability(customUrl);
//       },
//       {
//         message: 'This custom URL is already taken. Please choose another one.',
//       },
//     ),
// aboutUsImage: z.string().nullable(),
//   }),
// );

let lastCustomUrl, lastCustomUrlResult;
const aboutUsResolver = zodResolver(
  z.object({
    aboutUs: z.string().max(560, 'Maximum 560 characters allowed'),
    customUrl: z
      .string()
      .min(3, 'Custom URL must be at least 3 characters.')
      .max(64, 'Custom URL must not exceed 64 characters.')
      .regex(/^[A-Za-z0-9\-]+$/, {
        message: 'Custom URL may contain only letters, numbers, and hyphens.',
      })
      .superRefine(async (customUrl, ctx) => {
        if (customUrl.length < 3) {
          return;
        }
        try {
          const { available, message } = await debouncedCheckUrlAvailability(customUrl);
          if (!available) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: message || 'An unknown error occurred.',
            });
          }
        } catch (error) {
          if (error.response && error.response.data && error.response.data.errors?.customUrl) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: error.response.data.errors.customUrl[0],
            });
          } else {
            // Handle unexpected errors
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'An unexpected error occurred while checking the URL.',
            });
          }
        }
      }),
    aboutUsImage: z.string().nullable(),
  }),
);

const companyAdminStore = useCompanyAdminStore();
const donorStore = useDonorStore();

const truncatedDomain = computed(() => {
  let domain = donorStore.domain.trim('/');
  if (domain.length > 20) {
    domain = `${domain.slice(0, 20)}...`;
  }
  return domain + '/';
});

const initialValues = computed(() => ({
  aboutUs: companyAdminStore.company.aboutUs,
  customUrl: companyAdminStore.company.customUrl,
  aboutUsImage: companyAdminStore.company.aboutUsImage,
}));

const checkUrlAvailability = async (customUrl) => {
  await companyAdminStore.checkUrlAvailability(customUrl);
};

onBeforeMount(async () => {
  companyAdminStore.checkUrlAvailability;
});

onMounted(() => {
  companyAdminStore.aboutUsForm = aboutUsForm.value;

  const resizeObserver = new ResizeObserver(() => {
    // Handle resize events
  });

  resizeObserver.observe(document.body);

  // Clean up observer on component unmount
  onBeforeUnmount(() => {
    resizeObserver.disconnect();
  });
});
</script>
